import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";

import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import { useMutationWithError } from "../../../../../../../../utils/hooks/useMutationWithError";
import { bookingSummaryAnalyticsDataAtom } from "../../../../BookingSummary/atoms/analytics/bookingSummaryAnalyticsDataAtom";
import {
  DeleteSlotBookingInput,
  useCancelBookingMutation,
  useCancelBookingMutation$data,
} from "./__generated__/useCancelBookingMutation.graphql";

export const useCancelBooking = () => {
  const { showToast } = useToast();
  const [commitCancelBooking, isCancelingBooking] =
    useMutationWithError<useCancelBookingMutation>(cancelBookingMutationString);

  const analyticsData = useAtomValue(bookingSummaryAnalyticsDataAtom);
  const trackEvent = useTrackEvent();

  const deleteSlotBooking = (
    input: DeleteSlotBookingInput,
    successCallback?: () => void
  ) => {
    commitCancelBooking({
      variables: { input },
      onCompletedErrorCustom: (errors) => {
        const firstError = errors?.[0];

        showToast({
          title: t`Unable to cancel booking`,
          description:
            firstError?.code ||
            t`We couldn't cancel this booking. Please try again or contact the shop.`,
          semanticStyle: "error",
        });

        // No remaining errors for the default handler to show.
        return [];
      },
      onCompleted: (response: useCancelBookingMutation$data) => {
        if (response.responsePayload?.success) {
          trackEvent("BookingDetail.SlotBookingDeleted", analyticsData ?? {});
          if (successCallback) {
            successCallback();
          }
          showToast({
            title: t`Booking Cancelled`,
            semanticStyle: "success",
          });
        }
      },
    });
  };

  return [deleteSlotBooking, isCancelingBooking] as const;
};

const cancelBookingMutationString = graphql`
  mutation useCancelBookingMutation($input: DeleteSlotBookingInput!) {
    responsePayload: deleteSlotBooking(input: $input) {
      errors {
        code

        message
        field
      }
      success
      startSlot {
        ...TeeSheetAgendaConsistency_timeSlot
        ... on TimeSlot {
          id
          bookings {
            edges {
              node {
                id
                isDeleted
              }
            }
          }
        }
      }
    }
  }
`;
